import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { QueryPatternCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { EvaluationPathType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { QueryPatternSource } from "../../../../api/graphql";
import { InputNumber } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type QueryPatternCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  patternSelectedValue?: { displayId: string; id: string } | null;
  onPatternChange?: (value: string) => void;
  pattern: string;
  patternError?: string;
  pathTypeError?: string;
  pathTypeOptions: { id: string; displayId: string }[];
  pathType: EvaluationPathType;
  onPathTypeChange?: (value: EvaluationPathType) => void;
  variablesSelectedValue?: { displayId: string; id: string } | null;
  onVariablesChange?: (value: string) => void;
  variables: string;
  variablesError?: string;
  toolTemplateSelectedValue?: { displayId: string; id: string } | null;
  onToolTemplateChange?: (value: string) => void;
  toolTemplate: string;
  toolTemplateError?: string;
  argsTemplateSelectedValue?: { displayId: string; id: string } | null;
  onArgsTemplateChange?: (value: string) => void;
  argsTemplate: string;
  argsTemplateError?: string;
  variationsSelectedValue?: { displayId: string; id: string } | null;
  onVariationsChange?: (value: string) => void;
  variations: string;
  variationsError?: string;
  exampleInstantiationSelectedValue?: { displayId: string; id: string } | null;
  onExampleInstantiationChange?: (value: string) => void;
  exampleInstantiation: string;
  exampleInstantiationError?: string;
  sourceError?: string;
  sourceOptions: { id: string; displayId: string }[];
  source: QueryPatternSource;
  onSourceChange?: (value: QueryPatternSource) => void;
  qualityScoreSelectedValue?: { displayId: string; id: string } | null;
  onQualityScoreChange?: (value: number) => void;
  qualityScore: number;
  qualityScoreError?: string;
  usageCountSelectedValue?: { displayId: string; id: string } | null;
  onUsageCountChange?: (value: number) => void;
  usageCount: number;
  usageCountError?: string;
  isActiveSelectedValue?: { displayId: string; id: string } | null;
  onIsActiveChange?: (value: boolean) => void;
  isActive: boolean;
  isActiveError?: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notes: string;
  notesError?: string;
  queryPatternCreateStore: QueryPatternCreateStore;
};

export const QueryPatternCreateForm: React.FC<QueryPatternCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      pattern = "",
      patternError,
      onPatternChange = () => {},
      pathTypeError,
      pathTypeOptions = [],
      pathType = "" as EvaluationPathType,
      onPathTypeChange = () => {},
      variables = "{}",
      variablesError,
      onVariablesChange = () => {},
      toolTemplate = "",
      toolTemplateError,
      onToolTemplateChange = () => {},
      argsTemplate = "{}",
      argsTemplateError,
      onArgsTemplateChange = () => {},
      variations = "{}",
      variationsError,
      onVariationsChange = () => {},
      exampleInstantiation = "",
      exampleInstantiationError,
      onExampleInstantiationChange = () => {},
      sourceError,
      sourceOptions = [],
      source = "" as QueryPatternSource,
      onSourceChange = () => {},
      qualityScore = 0,
      qualityScoreError,
      onQualityScoreChange = () => {},
      usageCount = 0,
      usageCountError,
      onUsageCountChange = () => {},
      isActive = false,
      isActiveError,
      onIsActiveChange = () => {},
      notes = "",
      notesError,
      onNotesChange = () => {},
      queryPatternCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="QueryPattern"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "pattern":
                    onPatternChange?.(valor);
                    break;
                  case "pathType":
                    onPathTypeChange?.(valor);
                    break;
                  case "variables":
                    onVariablesChange?.(valor);
                    break;
                  case "toolTemplate":
                    onToolTemplateChange?.(valor);
                    break;
                  case "argsTemplate":
                    onArgsTemplateChange?.(valor);
                    break;
                  case "variations":
                    onVariationsChange?.(valor);
                    break;
                  case "exampleInstantiation":
                    onExampleInstantiationChange?.(valor);
                    break;
                  case "source":
                    onSourceChange?.(valor);
                    break;
                  case "qualityScore":
                    onQualityScoreChange?.(valor);
                    break;
                  case "usageCount":
                    onUsageCountChange?.(valor);
                    break;
                  case "isActive":
                    onIsActiveChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={queryPatternCreateStore.getCardErrors("null")}
              title={langStore.groupQueryPatternLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(queryPatternCreateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    queryPatternCreateStore.warnings["name"]?.message
                  }
                  label={langStore.QueryPatternNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.QueryPatternNameDescription}
                />

                <InputString
                  value={pattern}
                  error={Boolean(patternError)}
                  warning={
                    !patternError &&
                    Boolean(queryPatternCreateStore.warnings["pattern"])
                  }
                  helperText={
                    patternError ||
                    queryPatternCreateStore.warnings["pattern"]?.message
                  }
                  label={langStore.QueryPatternPatternLabel}
                  onChange={(value: string) => onPatternChange(value)}
                  description={langStore.QueryPatternPatternDescription}
                />
                <InputReference
                  id="pathType"
                  error={Boolean(pathTypeError)}
                  helperText={
                    pathTypeError ||
                    queryPatternCreateStore.warnings["pathType"]?.message
                  }
                  label={langStore.QueryPatternPathTypeLabel}
                  options={pathTypeOptions}
                  selectedValue={
                    pathType
                      ? {
                          id: pathType,
                          displayId:
                            pathTypeOptions.find(
                              (o: { id: string }) => o.id === pathType
                            )?.displayId || pathType,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onPathTypeChange(
                      (value?.id as EvaluationPathType) ||
                        ("" as EvaluationPathType)
                    )
                  }
                />

                <InputString
                  value={variables}
                  error={Boolean(variablesError)}
                  warning={
                    !variablesError &&
                    Boolean(queryPatternCreateStore.warnings["variables"])
                  }
                  helperText={
                    variablesError ||
                    queryPatternCreateStore.warnings["variables"]?.message
                  }
                  label={langStore.QueryPatternVariablesLabel}
                  onChange={(value: string) => onVariablesChange(value)}
                  description={langStore.QueryPatternVariablesDescription}
                />

                <InputString
                  value={toolTemplate}
                  error={Boolean(toolTemplateError)}
                  warning={
                    !toolTemplateError &&
                    Boolean(queryPatternCreateStore.warnings["toolTemplate"])
                  }
                  helperText={
                    toolTemplateError ||
                    queryPatternCreateStore.warnings["toolTemplate"]?.message
                  }
                  label={langStore.QueryPatternToolTemplateLabel}
                  onChange={(value: string) => onToolTemplateChange(value)}
                  description={langStore.QueryPatternToolTemplateDescription}
                />

                <InputString
                  value={argsTemplate}
                  error={Boolean(argsTemplateError)}
                  warning={
                    !argsTemplateError &&
                    Boolean(queryPatternCreateStore.warnings["argsTemplate"])
                  }
                  helperText={
                    argsTemplateError ||
                    queryPatternCreateStore.warnings["argsTemplate"]?.message
                  }
                  label={langStore.QueryPatternArgsTemplateLabel}
                  onChange={(value: string) => onArgsTemplateChange(value)}
                  description={langStore.QueryPatternArgsTemplateDescription}
                />

                <InputString
                  value={variations}
                  error={Boolean(variationsError)}
                  warning={
                    !variationsError &&
                    Boolean(queryPatternCreateStore.warnings["variations"])
                  }
                  helperText={
                    variationsError ||
                    queryPatternCreateStore.warnings["variations"]?.message
                  }
                  label={langStore.QueryPatternVariationsLabel}
                  onChange={(value: string) => onVariationsChange(value)}
                  description={langStore.QueryPatternVariationsDescription}
                />

                <InputString
                  value={exampleInstantiation}
                  error={Boolean(exampleInstantiationError)}
                  warning={
                    !exampleInstantiationError &&
                    Boolean(
                      queryPatternCreateStore.warnings["exampleInstantiation"]
                    )
                  }
                  helperText={
                    exampleInstantiationError ||
                    queryPatternCreateStore.warnings["exampleInstantiation"]
                      ?.message
                  }
                  label={langStore.QueryPatternExampleInstantiationLabel}
                  onChange={(value: string) =>
                    onExampleInstantiationChange(value)
                  }
                  description={
                    langStore.QueryPatternExampleInstantiationDescription
                  }
                />
                <InputReference
                  id="source"
                  error={Boolean(sourceError)}
                  helperText={
                    sourceError ||
                    queryPatternCreateStore.warnings["source"]?.message
                  }
                  label={langStore.QueryPatternSourceLabel}
                  options={sourceOptions}
                  selectedValue={
                    source
                      ? {
                          id: source,
                          displayId:
                            sourceOptions.find(
                              (o: { id: string }) => o.id === source
                            )?.displayId || source,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onSourceChange(
                      (value?.id as QueryPatternSource) ||
                        ("" as QueryPatternSource)
                    )
                  }
                />
                <InputNumber
                  value={qualityScore}
                  error={Boolean(qualityScoreError)}
                  warning={
                    !qualityScoreError &&
                    Boolean(queryPatternCreateStore.warnings["qualityScore"])
                  }
                  helperText={
                    qualityScoreError ||
                    queryPatternCreateStore.warnings["qualityScore"]?.message
                  }
                  allowNegative={false}
                  label={langStore.QueryPatternQualityScoreLabel}
                  decimalScale={0}
                  description={langStore.QueryPatternQualityScoreDescription}
                  onChange={(value: number) => onQualityScoreChange(value)}
                />
                <InputNumber
                  value={usageCount}
                  error={Boolean(usageCountError)}
                  warning={
                    !usageCountError &&
                    Boolean(queryPatternCreateStore.warnings["usageCount"])
                  }
                  helperText={
                    usageCountError ||
                    queryPatternCreateStore.warnings["usageCount"]?.message
                  }
                  allowNegative={false}
                  label={langStore.QueryPatternUsageCountLabel}
                  decimalScale={0}
                  onChange={(value: number) => onUsageCountChange(value)}
                />
                <InputBoolean
                  value={isActive}
                  error={Boolean(isActiveError)}
                  helperText={
                    isActiveError ||
                    queryPatternCreateStore.warnings["isActive"]?.message
                  }
                  label={langStore.QueryPatternIsActiveLabel}
                  onChange={(value: boolean) => onIsActiveChange(value)}
                />

                <InputText
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(queryPatternCreateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    queryPatternCreateStore.warnings["notes"]?.message
                  }
                  label={langStore.QueryPatternNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
